import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, ShieldCheck, FileText, Printer, Plus, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { openContractWindow, ContractButton } from "@/pages/ContractActions";
import { LayawayTab } from "@/components/LayawayTab";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
const today = () => new Date().toISOString().slice(0, 10);

// Abre una garantía guardada en ventana para ver/imprimir.
export async function openWarrantyWindow(wid, fmt = "thermal", print = true) {
  try {
    const { data } = await adminApi.get(`/admin/warranties/${wid}/html`, { params: { fmt } });
    const w = window.open("", "_blank", "width=520,height=800");
    if (!w) { toast.error("Permite las ventanas emergentes para ver/imprimir"); return; }
    w.document.open(); w.document.write(data.html); w.document.close(); w.focus();
    if (print) setTimeout(() => { try { w.print(); } catch (e) {} }, 500);
  } catch (e) { toast.error("No se pudo abrir la garantía"); }
}

// --- Crear garantía a partir de un equipo VENDIDO ---
function NewWarranty({ onDone, onBack }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [months, setMonths] = useState("3");
  const [notes, setNotes] = useState("");
  const [cust, setCust] = useState({ name: "", phone: "", email: "" });
  const [icloud, setIcloud] = useState({ created: false, email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const load = (s = q) => { const p = { status: "VENDIDO" }; if (s.trim()) p.q = s.trim(); adminApi.get("/admin/inventory", { params: p }).then((r) => setItems(r.data)).catch(() => toast.error("No se pudo cargar los equipos vendidos")); };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);
  const pick = (it) => { setSel(it); const s = it.sale || {}; setCust({ name: s.customer_name || "", phone: s.customer_phone || "", email: "" }); };
  const create = async () => {
    if (!sel) return;
    if (!cust.name.trim()) { toast.error("El nombre del cliente es obligatorio"); return; }
    if (icloud.created && (!icloud.email.trim() || !icloud.password.trim())) { toast.error("Correo y contraseña de iCloud son obligatorios"); return; }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/warranties", {
        inventory_id: sel.id, imei: sel.imei, months: Number(months), notes: notes || null, customer: cust,
        icloud_created: icloud.created,
        icloud_email: icloud.created ? icloud.email.trim() : null,
        icloud_password: icloud.created ? icloud.password : null,
      });
      toast.success(`Garantía ${data.warranty_number} creada`);
      setDone(data); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear la garantía"); } finally { setSaving(false); }
  };

  if (done) return (
    <div className="space-y-3" data-testid="warranty-created">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">Garantía <b>{done.warranty_number}</b> · {done.months} mes(es) · vence {done.warranty_end}</div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => openWarrantyWindow(done.id, "letter", false)} className="h-11 rounded-xl" data-testid="warranty-view"><FileText className="w-4 h-4 mr-2" /> Ver</Button>
        <Button onClick={() => openWarrantyWindow(done.id, "thermal", true)} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800" data-testid="warranty-print"><Printer className="w-4 h-4 mr-2" /> Imprimir</Button>
      </div>
      <Button variant="ghost" onClick={onBack} className="w-full h-10 rounded-xl">Volver al listado</Button>
    </div>
  );

  return (
    <div className="space-y-3">
      <button onClick={onBack} className="text-sm text-zinc-500 inline-flex items-center gap-1" data-testid="warranty-back"><ArrowLeft className="w-4 h-4" /> Volver</button>
      <p className="text-sm font-semibold">Garantía de iPhone — selecciona el equipo vendido</p>
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="warranty-item-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar por IMEI, modelo o ID de inventario..." className="h-11 rounded-xl pl-9" /></div>
        <Button variant="outline" onClick={() => load()} className="h-11 rounded-xl">Buscar</Button>
      </div>
      {!items ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        : items.length === 0 ? <p className="text-sm text-zinc-400 py-4 text-center">Sin equipos vendidos que coincidan.</p>
        : (
        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
          {items.map((it) => (
            <button key={it.id} data-testid={`warranty-item-${it.id}`} onClick={() => pick(it)} className={`w-full text-left rounded-xl border p-3 ${sel?.id === it.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}>
              <div className="flex justify-between"><span className="font-semibold text-zinc-900">{it.model} <span className="text-zinc-400 font-normal text-xs">{it.id}</span></span><span className="text-xs text-zinc-500">{it.exit_reason || "—"}</span></div>
              <p className="text-xs text-zinc-500">{[it.capacity, it.color].filter(Boolean).join(" · ")}{it.imei ? ` · IMEI ${it.imei}` : ""} · venta {money(it.sale_price)}</p>
            </button>
          ))}
        </div>
      )}
      {sel && (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/40 p-4 space-y-3" data-testid="warranty-form">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">{sel.model} {sel.capacity || ""} · IMEI {sel.imei || "—"} · venta {money(sel.sale_price)} · venta cód {sel.sale_reference || "—"}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="flex flex-col gap-0.5"><span className="text-[10px] text-zinc-400 px-1">Meses de garantía *</span>
              <select data-testid="warranty-months" value={months} onChange={(e) => setMonths(e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">{["1", "3", "6", "12"].map((m) => <option key={m} value={m}>{m} mes(es)</option>)}</select></label>
            <Input data-testid="warranty-cust-name" placeholder="Cliente *" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className="h-10 rounded-xl" />
            <Input data-testid="warranty-cust-phone" placeholder="Teléfono" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} className="h-10 rounded-xl" />
            <Input data-testid="warranty-cust-email" placeholder="Correo" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} className="h-10 rounded-xl" />
            <Input data-testid="warranty-notes" placeholder="Observaciones" value={notes} onChange={(e) => setNotes(e.target.value)} className="h-10 rounded-xl sm:col-span-4" />
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" data-testid="warranty-icloud-check" checked={icloud.created} onChange={(e) => setIcloud({ ...icloud, created: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
              <span className="text-sm font-medium text-zinc-800">Creamos cuenta de iCloud</span>
            </label>
            {icloud.created && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input data-testid="warranty-icloud-email" placeholder="Correo de la cuenta *" value={icloud.email} onChange={(e) => setIcloud({ ...icloud, email: e.target.value })} className="h-10 rounded-xl" />
                <Input data-testid="warranty-icloud-password" type="text" placeholder="Contraseña *" value={icloud.password} onChange={(e) => setIcloud({ ...icloud, password: e.target.value })} className="h-10 rounded-xl" />
              </div>
            )}
          </div>
          <Button onClick={create} disabled={saving} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="warranty-create-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear e Imprimir garantía"}</Button>
        </div>
      )}
    </div>
  );
}

// --- Crear contrato de recepción Trade-In (reutiliza el contrato existente) ---
function NewReception({ onBack }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  useEffect(() => { adminApi.get("/admin/quotes").then((r) => setList((r.data || []).filter((x) => ["TRADE-IN REALIZADO", "CASH REALIZADO"].includes(x.operation_status)))).catch(() => toast.error("No se pudo cargar Trade-Ins")); }, []);
  const term = q.trim().toLowerCase();
  const rows = (list || []).filter((x) => !term || [x.id, x.model, x.imei, x.contact?.name].some((f) => f && String(f).toLowerCase().includes(term)));
  return (
    <div className="space-y-3">
      <button onClick={onBack} className="text-sm text-zinc-500 inline-flex items-center gap-1" data-testid="reception-back"><ArrowLeft className="w-4 h-4" /> Volver</button>
      <p className="text-sm font-semibold">Contrato de recepción Trade‑In — selecciona la operación</p>
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input data-testid="reception-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código TI, modelo, IMEI o cliente..." className="h-11 rounded-xl pl-9" /></div>
      {!list ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        : rows.length === 0 ? <p className="text-sm text-zinc-400 py-4 text-center">Sin Trade-Ins realizados. Completa un Trade-In primero.</p>
        : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {rows.map((x) => (
            <div key={x.id} data-testid={`reception-row-${x.id}`} className="rounded-xl border border-zinc-200 p-3 flex items-center justify-between gap-3">
              <div><p className="font-semibold text-zinc-900">{x.id} <span className="text-zinc-400 font-normal text-xs">{x.model} {x.capacity || ""}</span></p>
                <p className="text-xs text-zinc-500">{x.contact?.name || "—"}{x.imei ? ` · IMEI ${x.imei}` : ""} · {money(x.price)}</p></div>
              <ContractButton operationType="trade_in" operation={x} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DocsPanel = () => {
  const [q, setQ] = useState("");
  const [type, setType] = useState("ALL");
  const [contracts, setContracts] = useState(null);
  const [warranties, setWarranties] = useState(null);
  const [mode, setMode] = useState(null); // null | 'choose' | 'reception' | 'warranty'
  const load = () => {
    adminApi.get("/admin/contracts").then((r) => setContracts(r.data)).catch(() => setContracts([]));
    adminApi.get("/admin/warranties").then((r) => setWarranties(r.data)).catch(() => setWarranties([]));
  };
  useEffect(() => { load(); }, []);

  const rows = [];
  (contracts || []).forEach((c) => rows.push({
    kind: "contract", id: c.id, code: c.contract_number, type: c.operation_type === "trade_in" ? "Contrato recepción Trade‑In" : "Contrato compra directa",
    client: (c.customer || {}).name, iphone: `${(c.equipment || {}).model || ""} ${(c.equipment || {}).capacity || ""}`.trim(),
    imei: (c.equipment || {}).imei, date: c.created_at, status: "—", isWarranty: false,
  }));
  (warranties || []).forEach((w) => {
    const active = w.warranty_end && w.warranty_end >= today();
    rows.push({ kind: "warranty", id: w.id, code: w.warranty_number, type: "Garantía de iPhone", client: (w.customer || {}).name,
      iphone: `${(w.iphone || {}).model || ""} ${(w.iphone || {}).capacity || ""}`.trim(), imei: w.imei, date: w.created_at,
      status: active ? "Vigente" : "Vencida", isWarranty: true });
  });
  const term = q.trim().toLowerCase();
  const filtered = rows
    .filter((r) => type === "ALL" || (type === "CONTRACTS" && r.kind === "contract") || (type === "WARRANTIES" && r.kind === "warranty"))
    .filter((r) => !term || [r.code, r.client, r.imei].some((f) => f && String(f).toLowerCase().includes(term)))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const loading = contracts === null || warranties === null;

  return (
    <div data-testid="warranty-contracts-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><p className="font-semibold text-zinc-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Garantías y Contratos</p>
          <p className="text-xs text-zinc-500 mt-1">Gestiona contratos de recepción y garantías de los iPhones.</p></div>
        <Button onClick={() => setMode("choose")} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 shrink-0" data-testid="doc-new-btn"><Plus className="w-4 h-4 mr-1" /> Nueva Garantía / Contrato</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente, IMEI o código..." className="h-11 rounded-xl pl-9" /></div>
        <div className="flex gap-1.5">
          {[["ALL", "Todos"], ["CONTRACTS", "Contratos"], ["WARRANTIES", "Garantías"]].map(([k, l]) => (
            <button key={k} data-testid={`doc-filter-${k}`} onClick={() => setType(k)} className={`px-3 h-11 rounded-xl text-sm font-medium ${type === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        : filtered.length === 0 ? <p className="text-sm text-zinc-400 py-8 text-center" data-testid="doc-empty">Sin documentos.</p>
        : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200">
          <table className="w-full text-xs" data-testid="doc-table">
            <thead className="bg-zinc-50 text-zinc-500 text-left"><tr>{["Código", "Tipo", "Cliente", "iPhone", "IMEI", "Fecha", "Estado", ""].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.kind + r.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`doc-row-${r.code}`}>
                  <td className="px-3 py-2 font-mono text-zinc-600">{r.code}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.isWarranty ? "bg-indigo-100 text-indigo-700" : "bg-zinc-200 text-zinc-700"}`}>{r.type}</span></td>
                  <td className="px-3 py-2">{r.client || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.iphone || "—"}</td>
                  <td className="px-3 py-2 font-mono">{r.imei || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">{r.isWarranty ? <span className={`text-[10px] font-semibold ${r.status === "Vigente" ? "text-emerald-600" : "text-red-600"}`}>{r.status}</span> : "—"}</td>
                  <td className="px-3 py-2"><div className="flex gap-2 whitespace-nowrap">
                    <button data-testid={`doc-view-${r.code}`} onClick={() => (r.isWarranty ? openWarrantyWindow(r.id, "letter", false) : openContractWindow(r.id, "letter", false))} className="text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Ver</button>
                    <button data-testid={`doc-print-${r.code}`} onClick={() => (r.isWarranty ? openWarrantyWindow(r.id, "thermal", true) : openContractWindow(r.id, "thermal", true))} className="text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> Imprimir</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-new-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Garantía / Contrato</DialogTitle>
            <DialogDescription>Selecciona el tipo de documento a generar.</DialogDescription>
          </DialogHeader>
          {mode === "choose" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button data-testid="doc-choose-reception" onClick={() => setMode("reception")} className="rounded-2xl border border-zinc-200 hover:border-zinc-900 p-4 text-left transition-all">
                <FileText className="w-5 h-5 mb-2" /><p className="font-semibold">Contrato de recepción Trade‑In</p><p className="text-xs text-zinc-500 mt-1">Constancia de recepción del equipo entregado por el cliente en un Trade‑In.</p></button>
              <button data-testid="doc-choose-warranty" onClick={() => setMode("warranty")} className="rounded-2xl border border-zinc-200 hover:border-zinc-900 p-4 text-left transition-all">
                <ShieldCheck className="w-5 h-5 mb-2" /><p className="font-semibold">Garantía de iPhone</p><p className="text-xs text-zinc-500 mt-1">Garantía del equipo vendido (venta simple o equipo que llegó por Trade‑In).</p></button>
            </div>
          )}
          {mode === "reception" && <NewReception onBack={() => setMode("choose")} />}
          {mode === "warranty" && <NewWarranty onBack={() => setMode("choose")} onDone={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const WarrantyContractsTab = () => {
  const [section, setSection] = useState("docs");
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 overflow-x-auto pb-0.5" data-testid="warranty-section-tabs">
        <button data-testid="warranty-section-docs" onClick={() => setSection("docs")} className={`shrink-0 h-9 px-4 rounded-full text-sm font-medium ${section === "docs" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>Garantías y Contratos</button>
        <button data-testid="warranty-section-layaway" onClick={() => setSection("layaway")} className={`shrink-0 h-9 px-4 rounded-full text-sm font-medium ${section === "layaway" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>Apartados y Crédito</button>
      </div>
      {section === "docs" ? <DocsPanel /> : <LayawayTab />}
    </div>
  );
};
